/*
  RM 单选筛选条（第一版，2026-07-29）

  独立于主题原有的 facets.js：原文件的 renderAdditionalElements 依赖移动端抽屉的 DOM
  (#FacetFiltersFormMobile / facet-drawer)，复用会在没有那套结构时抛错并中断商品网格更新。

  行为：
  - 每个大类一组同名 radio，天然互斥 = 单选；提交前丢弃空值（"All" 选项）
  - 桌面端选择后立即刷新；移动端在抽屉内选完后由 Show results 一次提交
  - 用 section_id 拉当前 section，替换商品网格与筛选条本身
  - 新请求会取消旧请求，浏览器前进/后退只重渲染、不重复写历史
*/

class RmFacets extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('#RmFacetsForm');
    if (!this.form) return;

    this.sectionId = this.dataset.sectionId;
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    if (RmFacets.desktopExpanded === null) {
      RmFacets.desktopExpanded = this.dataset.defaultCollapsed !== 'true';
    }

    this.bindEvents();
    this.syncDrawerState();
  }

  bindEvents() {
    this.form.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]')) {
        this.syncOptionState(event.target);
        if (!this.mobileQuery.matches) this.submit();
        return;
      }

      if (event.target.matches('select')) {
        this.form.querySelectorAll('select[name="sort_by"]').forEach((select) => {
          select.value = event.target.value;
        });
        this.submit();
      }
    });

    this.addEventListener('click', (event) => {
      if (event.target.closest('.rm-facets__toggle')) {
        this.toggleDesktopFilters();
        return;
      }

      if (event.target.closest('.rm-facets__mopen')) {
        this.openDrawer(true);
        return;
      }

      if (event.target.closest('.rm-facets__close') || event.target.closest('.rm-facets__scrim')) {
        this.openDrawer(false, { discardChanges: true });
        return;
      }

      if (event.target.closest('[data-apply]')) {
        this.submit({ closeDrawer: this.mobileQuery.matches });
        return;
      }

      const clear = event.target.closest('[data-clear]');
      if (clear) {
        this.clearGroup(clear.dataset.clear);
        return;
      }

      if (event.target.closest('[data-clear-all]')) {
        this.clearAll();
        return;
      }

      const chip = event.target.closest('[data-remove]');
      if (chip) {
        this.clearGroup(chip.dataset.remove, chip.dataset.removeValue);
        return;
      }

      if (event.target.closest('[data-remove-price]')) {
        this.clearGroup('rm_price');
      }
    });

    this.onKeydown = (event) => {
      if (!this.mobileQuery.matches || !RmFacets.drawerOpen) return;

      if (event.key === 'Escape') {
        this.openDrawer(false, { discardChanges: true });
        return;
      }

      if (event.key === 'Tab') this.trapDrawerFocus(event);
    };
    document.addEventListener('keydown', this.onKeydown);

    this.onBreakpointChange = (event) => {
      if (!event.matches && RmFacets.drawerOpen) {
        this.openDrawer(false, { restoreFocus: false });
      }
      this.syncDrawerState();
    };
    this.mobileQuery.addEventListener('change', this.onBreakpointChange);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    this.mobileQuery?.removeEventListener('change', this.onBreakpointChange);
    clearTimeout(this.scrimTimer);

    queueMicrotask(() => {
      if (!document.querySelector('rm-facets')) {
        RmFacets.drawerOpen = false;
        document.body.style.overflow = '';
      }
    });
  }

  syncDrawerState() {
    const isMobile = this.mobileQuery.matches;
    const open = isMobile && RmFacets.drawerOpen;
    const trigger = this.querySelector('.rm-facets__mopen');
    const desktopToggle = this.querySelector('.rm-facets__toggle');
    const panels = this.querySelector('.rm-facets__panels');
    const scrim = this.querySelector('.rm-facets__scrim');

    this.classList.toggle('is-open', open);
    if (trigger) trigger.setAttribute('aria-expanded', String(open));

    if (panels) {
      if (isMobile) {
        panels.inert = !open;
        panels.setAttribute('role', 'dialog');
        panels.setAttribute('aria-modal', 'true');
        panels.setAttribute('aria-hidden', String(!open));
      } else {
        const expanded = RmFacets.desktopExpanded;
        this.classList.toggle('is-filters-open', expanded);
        if (desktopToggle) desktopToggle.setAttribute('aria-expanded', String(expanded));
        panels.inert = !expanded;
        panels.removeAttribute('role');
        panels.removeAttribute('aria-modal');
        panels.setAttribute('aria-hidden', String(!expanded));
      }
    }

    if (scrim) {
      if (open) scrim.hidden = false;
      if (!isMobile) scrim.hidden = true;
    }

    document.body.style.overflow = open ? 'hidden' : '';
  }

  toggleDesktopFilters() {
    if (this.mobileQuery.matches) return;
    RmFacets.desktopExpanded = !RmFacets.desktopExpanded;
    this.syncDrawerState();
  }

  openDrawer(open, { restoreFocus = true, discardChanges = false } = {}) {
    const trigger = this.querySelector('.rm-facets__mopen');
    const close = this.querySelector('.rm-facets__close');
    const scrim = this.querySelector('.rm-facets__scrim');

    clearTimeout(this.scrimTimer);
    if (open) {
      this.drawerOpener = document.activeElement;
      if (scrim) scrim.hidden = false;
    } else if (discardChanges) {
      this.resetForm();
    }

    RmFacets.drawerOpen = open;
    this.syncDrawerState();

    if (open) {
      requestAnimationFrame(() => close?.focus());
      return;
    }

    this.scrimTimer = window.setTimeout(() => {
      if (!RmFacets.drawerOpen && scrim) scrim.hidden = true;
    }, 360);

    if (restoreFocus) {
      const focusTarget = this.drawerOpener?.isConnected ? this.drawerOpener : trigger;
      focusTarget?.focus();
    }
  }

  trapDrawerFocus(event) {
    const panels = this.querySelector('.rm-facets__panels');
    if (!panels) return;

    const focusable = Array.from(
      panels.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.getClientRects().length > 0);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !panels.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !panels.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  }

  syncOptionState(input) {
    const group = input.closest('.rm-opts');
    if (!group) return;

    group.querySelectorAll('.rm-opt').forEach((option) => {
      option.classList.toggle('is-on', Boolean(option.querySelector('input')?.checked));
    });
  }

  syncAllOptionStates() {
    this.form.querySelectorAll('.rm-opts').forEach((group) => {
      group.querySelectorAll('.rm-opt').forEach((option) => {
        option.classList.toggle('is-on', Boolean(option.querySelector('input')?.checked));
      });
    });
  }

  resetForm() {
    this.form.reset();
    this.form.querySelectorAll('[data-active-filter]').forEach((input) => {
      input.disabled = false;
    });
    this.syncAllOptionStates();
  }

  clearGroup(paramName, value) {
    const hiddenValues = Array.from(this.form.querySelectorAll('[data-active-filter]')).filter((input) => {
      return input.name === paramName && (value === undefined || input.value === value);
    });
    hiddenValues.forEach((input) => { input.disabled = true; });
    const all = this.form.querySelector(`input[name="${CSS.escape(paramName)}"][value=""]`);
    if (!all && hiddenValues.length === 0) return;

    if (all) {
      all.checked = true;
      this.syncOptionState(all);
    }
    if (this.mobileQuery.matches && RmFacets.drawerOpen) return;
    this.submit();
  }

  clearAll() {
    if (this.mobileQuery.matches && RmFacets.drawerOpen) {
      this.form.querySelectorAll('[data-active-filter]').forEach((input) => {
        input.disabled = true;
      });
      this.form.querySelectorAll('.rm-opts input[type="radio"][value=""]').forEach((input) => {
        input.checked = true;
      });
      this.syncAllOptionStates();
      return;
    }

    RmFacets.drawerOpen = false;
    document.body.style.overflow = '';
    const sort = this.form.querySelector('select[name="sort_by"]');
    const params = new URLSearchParams();
    this.form.querySelectorAll('input[type="hidden"][name]').forEach((input) => {
      if (input.value && !input.name.startsWith('filter.')) params.append(input.name, input.value);
    });
    if (sort && sort.value) params.set('sort_by', sort.value);
    this.addPersistentParams(params);
    this.render(params.toString());
  }

  addPersistentParams(params) {
    const collectionHandle = this.dataset.collectionHandle || '';
    if (collectionHandle) params.set('collection', collectionHandle);
    else params.delete('collection');

    const currentParams = new URLSearchParams(window.location.search);
    if (params.has('q') && currentParams.has('options[prefix]')) {
      params.set('options[prefix]', currentParams.get('options[prefix]'));
    }
    const previewThemeId = currentParams.get('preview_theme_id');
    if (previewThemeId) params.set('preview_theme_id', previewThemeId);
    return params;
  }

  buildParams() {
    const params = new URLSearchParams();
    new FormData(this.form).forEach((value, key) => {
      const trimmed = typeof value === 'string' ? value.trim() : value;
      if (trimmed === '') return;

      // 价格档位用 "gte:lte" 编码在一个 radio 上（保证单选），提交前拆回 Shopify 的两个参数
      if (key === 'rm_price') {
        const [gte, lte] = trimmed.split(':');
        if (gte && gte !== '0') params.append('filter.v.price.gte', gte);
        if (lte && lte !== '0') params.append('filter.v.price.lte', lte);
        return;
      }

      if (key === 'sort_by') {
        params.set(key, trimmed);
        return;
      }

      params.append(key, trimmed);
    });
    return this.addPersistentParams(params).toString();
  }

  submit({ closeDrawer = false } = {}) {
    if (closeDrawer) this.openDrawer(false, { discardChanges: false });
    this.render(this.buildParams());
  }

  render(
    searchParams,
    { updateHistory = true, resultsUrl, historyUrl, collectionHandle, resultView } = {}
  ) {
    RmFacets.requestController?.abort();
    const controller = new AbortController();
    RmFacets.requestController = controller;

    const requestParams = new URLSearchParams(searchParams);
    requestParams.delete('collection');
    requestParams.set('section_id', this.sectionId);
    const activeResultsUrl = resultsUrl || this.dataset.resultsUrl || window.location.pathname;
    const activeHistoryUrl = historyUrl || this.dataset.historyUrl || window.location.pathname;
    const activeCollectionHandle = collectionHandle !== undefined
      ? collectionHandle
      : (this.dataset.collectionHandle || '');
    const activeResultView = resultView !== undefined
      ? resultView
      : (this.dataset.resultView || '');
    if (activeResultView) requestParams.set('view', activeResultView);
    const requestUrl = new URL(activeResultsUrl, window.location.origin);
    requestUrl.search = requestParams.toString();
    const url = `${requestUrl.pathname}${requestUrl.search}`;
    this.classList.add('is-loading');
    this.setStatus('');

    const grid = document.getElementById('ProductGridContainer');
    const collection = grid ? grid.querySelector('.collection') : null;
    if (collection) collection.classList.add('loading');

    return fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (controller.signal.aborted || RmFacets.requestController !== controller) return;

        const parsed = new DOMParser().parseFromString(text, 'text/html');

        const newGrid = parsed.getElementById('ProductGridContainer');
        const newFacets = parsed.querySelector('rm-facets');
        if (!newGrid || !newFacets || !grid) throw new Error('Incomplete filter response');

        grid.innerHTML = newGrid.innerHTML;
        if (newFacets) {
          newFacets.dataset.resultsUrl = activeResultsUrl;
          newFacets.dataset.historyUrl = activeHistoryUrl;
          newFacets.dataset.collectionHandle = activeCollectionHandle;
          newFacets.dataset.resultView = activeResultView;
          this.replaceWith(newFacets);
        }

        if (updateHistory) {
          history.pushState(
            { searchParams },
            '',
            `${activeHistoryUrl}${searchParams ? '?'.concat(searchParams) : ''}`
          );
        }

        document.dispatchEvent(new CustomEvent('collection:reloaded'));
      })
      .catch((error) => {
        if (error.name === 'AbortError' || RmFacets.requestController !== controller) return;
        console.error('[rm-facets]', error);
        this.resetForm();
        this.setStatus('Filters could not update. Please try again.');
      })
      .finally(() => {
        if (RmFacets.requestController === controller) {
          this.classList.remove('is-loading');
          if (collection) collection.classList.remove('loading');
          grid?.querySelector('.collection')?.classList.remove('loading');
          RmFacets.requestController = null;
        }
      });
  }

  setStatus(message) {
    const status = this.querySelector('.rm-facets__status');
    if (!status) return;
    status.textContent = message;
    status.hidden = message === '';
  }
}

RmFacets.drawerOpen = false;
RmFacets.desktopExpanded = null;
RmFacets.requestController = null;

if (!customElements.get('rm-facets')) {
  customElements.define('rm-facets', RmFacets);
}

window.addEventListener('popstate', () => {
  if (document.querySelector('anniversary-category-tabs')) return;
  const facets = document.querySelector('rm-facets');
  if (!facets) return;
  facets.render(window.location.search.slice(1), { updateHistory: false });
});
